import React, { FC, ReactNode, useEffect, useRef } from "react";
import { FieldCheckbox } from "../../fields/FieldCheckbox";
import { Alert } from "../../alerts/Alert";
import {
  getMapFromCustomText,
  TemplateAsComponent,
} from "../../TemplateAsComponent";
import {
  FormsTosLocalization,
  CustomText,
} from "../../../types/graphql/generated";
import { scrollTo } from "../../../helpers/scroll";
import { colors } from "../../../styles/variables";
import { StyledBody, StyledTerm, StyledTerms } from "./index.styled";

interface TermsAndConditionsProps {
  children?: ReactNode;
  localization?: FormsTosLocalization | null;
  shippingAcknowledgmentMessage?: string;
  submitFailed?: boolean;
  terms: CustomText[];
  type: "info" | "optin";
  valid?: boolean;
}

interface GetSortedTermsByLocation {
  location: "review" | "signup" | "login";
  customTexts: CustomText[];
  hasGasExchangeItems?: boolean;
}

const SHIPPING_ACKNOWLEDGMENT_ERROR = "Shipping fee acknowledgment is required";
const SHIPPING_ACKNOWLEDGEMENT_TYPE = "shipping_acknowledgement";

export const getSortedTermsByLocation = ({
  location,
  customTexts,
  hasGasExchangeItems,
}: GetSortedTermsByLocation) => {
  return customTexts
    .filter((customText) => {
      // if has gas exchange items show gas exchange terms
      if (hasGasExchangeItems && customText.type === "gasExchange") return true;
      return customText.type === "terms" && customText.location === location;
    })
    .sort((a, b) => (a.position || 0) - (b.position || 0));
};

const Term: FC<{ term: CustomText; withCheckboxes?: boolean }> = ({
  term,
  withCheckboxes,
}) => {
  return (
    <StyledBody withCheckboxes={withCheckboxes}>
      {term.body.map((section, i) => {
        return (
          <StyledTerm location={term.location} index={i} key={section}>
            <TemplateAsComponent
              template={section}
              map={getMapFromCustomText({
                customText: term,
                underlineLinks: true,
              })}
            />
          </StyledTerm>
        );
      })}
    </StyledBody>
  );
};

export const TermsAndConditions: FC<TermsAndConditionsProps> = (props) => {
  const { children, terms, type, shippingAcknowledgmentMessage } = props;
  const hasRequiredCheckboxes =
    type === "optin" || !!shippingAcknowledgmentMessage;
  const termsRef = useRef<HTMLDivElement>(null);
  const submitFailed = hasRequiredCheckboxes ? props.submitFailed : false;
  const valid = hasRequiredCheckboxes ? props.valid : true;
  const localization = hasRequiredCheckboxes ? props.localization : null;

  useEffect(() => {
    if (submitFailed && !valid) scrollTo({ ref: termsRef });
  }, [submitFailed, valid]);
  const {
    errorText = "You must read and agree to the terms of service before proceeding",
  } = localization || {};

  return (
    <StyledTerms
      withCheckboxes={hasRequiredCheckboxes}
      ref={hasRequiredCheckboxes ? termsRef : undefined}
    >
      {submitFailed && !valid && (
        <Alert type="error" bold>
          {shippingAcknowledgmentMessage
            ? SHIPPING_ACKNOWLEDGMENT_ERROR
            : errorText}
        </Alert>
      )}
      {terms.map((term) =>
        type === "optin" ? (
          <FieldCheckbox
            key={term.type}
            label={<Term term={term} withCheckboxes />}
            name={term.type}
            color={colors.gray600}
            fontSize="13px"
          />
        ) : (
          <Term term={term} key={term.type} />
        )
      )}
      {shippingAcknowledgmentMessage && (
        <FieldCheckbox
          label={shippingAcknowledgmentMessage}
          name={SHIPPING_ACKNOWLEDGEMENT_TYPE}
          color={colors.gray600}
          fontSize="13px"
        />
      )}
      {children}
    </StyledTerms>
  );
};
